"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

export type ReadingSize = "small" | "medium" | "large";

interface ReadingSizeContextType {
  readingSize: ReadingSize;
  setReadingSize: (size: ReadingSize) => void;
}

const ReadingSizeContext = createContext<ReadingSizeContextType | undefined>(undefined);

const STORAGE_KEY = "tradednews-reading-size";

export function ReadingSizeProvider({ children }: { children: React.ReactNode }) {
  const [readingSize, setReadingSizeState] = useState<ReadingSize>("medium");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ReadingSize | null;
    if (stored && ["small", "medium", "large"].includes(stored)) {
      setReadingSizeState(stored);
    }
    setIsInitialized(true);
  }, []);

  // Apply to document and persist when changed
  useEffect(() => {
    if (!isInitialized) return;

    const root = document.documentElement;
    root.setAttribute("data-reading-size", readingSize);
    localStorage.setItem(STORAGE_KEY, readingSize);
  }, [readingSize, isInitialized]);

  const setReadingSize = useCallback((size: ReadingSize) => {
    setReadingSizeState(size);
  }, []);

  return (
    <ReadingSizeContext.Provider value={{ readingSize, setReadingSize }}>
      {children}
    </ReadingSizeContext.Provider>
  );
}

export function useReadingSize() {
  const context = useContext(ReadingSizeContext);
  if (context === undefined) {
    throw new Error("useReadingSize must be used within a ReadingSizeProvider");
  }
  return context;
}
